/**
 * Client-side decision logic for whether to show a SurveyMonkey exit survey when a
 * particular product is removed from the cart. Kept as a small, framework-agnostic module
 * so both `store-vue` and `order-vue` share one source of truth. Each survey is identified
 * by a `surveyId` and gets its own independent 24h suppression window — showing one survey
 * never suppresses another.
 *
 * It does NOT render anything and does NOT talk to SurveyMonkey — the caller mounts the
 * `DpSurveySubscriptionModal` overlay (which lazy-loads the survey iframe) when this
 * returns true. Suppression is keyed on OPEN, not submission (submission is unobservable
 * cross-origin).
 */

export const MEMBERSHIP_REMOVAL_SURVEY_URL = 'https://www.surveymonkey.com/r/XPBJFRR';

export const DESIGNER_TAG_REMOVAL_SURVEY_URL = 'https://www.surveymonkey.com/r/2S2VGJJ';

export const MEMBERSHIP_REMOVAL_SURVEY_ID = 'membership_removal';

export const DESIGNER_TAG_REMOVAL_SURVEY_ID = 'designer_tag_removal';

const SURVEY_SUPPRESS_KEYS = {
  [MEMBERSHIP_REMOVAL_SURVEY_ID]: 'dp_membership_removal_survey_last_shown',
};

export const SURVEY_SUPPRESS_KEY = SURVEY_SUPPRESS_KEYS[MEMBERSHIP_REMOVAL_SURVEY_ID];

/** Suppression window: do not re-show a survey within this many milliseconds (24h). */
export const SURVEY_SUPPRESS_MS = 24 * 60 * 60 * 1000;

function suppressKeyFor(surveyId) {
  return SURVEY_SUPPRESS_KEYS[surveyId] || `dp_${surveyId}_survey_last_shown`;
}

/**
 * Whether the given survey is currently suppressed by the 24h window.
 *
 * Fails OPEN: if localStorage is unavailable or the stored value is unusable, we treat the
 * survey as NOT suppressed (better to risk showing it than to silently never show it).
 *
 * @param {string} surveyId - Which survey's window to check.
 * @returns {boolean}
 */
export function isSurveySuppressed(surveyId) {
  try {
    const raw = window.localStorage.getItem(suppressKeyFor(surveyId));
    if (!raw) {
      return false;
    }
    const lastShown = Number(raw);
    if (!Number.isFinite(lastShown)) {
      return false;
    }
    return Date.now() - lastShown < SURVEY_SUPPRESS_MS;
  } catch (e) {
    // Private mode / storage disabled — fail open.
    return false;
  }
}

/**
 * Record that the given survey was shown now, starting its 24h suppression window.
 *
 * Best-effort: storage failures are swallowed (the survey simply won't be suppressed next
 * time, which is acceptable and non-blocking).
 *
 * @param {string} surveyId - Which survey's window to start.
 */
export function markSurveyShown(surveyId) {
  try {
    window.localStorage.setItem(suppressKeyFor(surveyId), String(Date.now()));
  } catch (e) {
    // No-op: suppression is a nicety, never a blocker.
  }
}

/**
 * Decide whether a cart-removal survey should be shown.
 *
 * When it returns true it has ALREADY recorded the show (suppression window starts now), so
 * the caller must open the dialog. This keeps the "at most once per window" guarantee even
 * if the caller mounts the overlay asynchronously.
 *
 * @param {Object} options
 * @param {boolean} options.enabled - The evaluated feature flag value for this survey.
 * @param {string} options.surveyId
 * @returns {boolean} true if the caller should open the survey dialog.
 */
export function shouldShowSurvey({ enabled, surveyId } = {}) {
  if (!enabled) {
    return false;
  }
  if (isSurveySuppressed(surveyId)) {
    return false;
  }
  markSurveyShown(surveyId);
  return true;
}
